// CARDNUDGE1 (docs/build-briefs/CARDNUDGE1-finish-card-payments-card.md): the one card at the top of Open Tabs, the
// location console's home, that says plainly how to finish card payments. It reads the same card payments read as
// Settings (getCardPayments) and uses the same onboarding link and safety check (requestOnboardingTarget in
// cardPayments.ts). A session without admin.payments.onboard, or without the read, never sees it. Gone once card
// payments are on. Labels and states only. No account id, no link kept, all text via textContent.

import { getCardPayments, postCardPaymentsReturn } from "./api.ts";
import {
  mintOnboardingLink,
  openOnboardingTab,
  requestOnboardingTarget,
  reviewPoller,
  TAB_BLOCKED_LINE,
  watchReturnFromStripe,
} from "./cardPayments.ts";
import {
  CARD_NUDGE_BUTTON,
  REVIEWING_LINE,
  cardNudgeView,
  type CardNudgeView,
  type CardPaymentsForLocationDto,
} from "./cardPaymentsModel.ts";

export interface CardNudgeDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds admin.terminal.view (the read) and admin.payments.onboard. Both are needed to show the card. */
  canShow: () => boolean;
  /** True while the session this card serves is still the live one. */
  isCurrent: () => boolean;
  /** STRIPETAB1: runs in the click, just before the Stripe tab opens (Home clears the wizard's return marker). */
  beforeOpen?: () => void;
  /** Gets whatever `missingStep` needs to answer (the location's own unconfirmed steps). */
  prepareMissingStep: () => Promise<void>;
  /** What the location still has to fill in before an account can be made, with where to do it, or null. */
  missingStep: () => { line: string; linkLabel: string; open: () => void } | null;
}

export interface CardNudge {
  /** Reads this location's card payments fresh and draws the card, or hides it. */
  refresh(): Promise<void>;
  reset(): void;
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  if (className !== null) created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

export function mountCardNudge(root: HTMLElement, deps: CardNudgeDeps): CardNudge {
  let sequence = 0;
  let mode: "test" | "live" | undefined;
  let errorLine = "";
  let opening = false;
  let lastView: CardNudgeView | null = null;
  let lastStep: ReturnType<CardNudgeDeps["missingStep"]> = null;
  root.hidden = true;
  // STRIPETAB1: back on Home after Stripe's tab, have the server read the account once, then redraw the card.
  watchReturnFromStripe(() => {
    if (lastView === null || root.hidden || opening || !deps.canShow() || !deps.isCurrent()) return;
    void (async () => {
      await postCardPaymentsReturn(deps.apiBaseUrl(), deps.tenantId());
      await refresh();
    })();
  });

  // STRIPETAB1: while the card says Stripe is reviewing, read again every 30 seconds while this tab is visible.
  const schedulePoll = reviewPoller(
    () => lastView !== null && !root.hidden && lastView.line === REVIEWING_LINE,
    () => {
      if (!opening && deps.canShow() && deps.isCurrent()) void refresh();
    },
  );

  function reset(): void {
    sequence += 1;
    mode = undefined;
    errorLine = "";
    opening = false;
    lastView = null;
    lastStep = null;
    root.replaceChildren();
    root.hidden = true;
  }

  function render(): void {
    root.replaceChildren();
    if (lastView === null) {
      root.hidden = true;
      return;
    }
    const view = lastView;
    root.hidden = false;
    root.dataset["state"] = view.state;
    const head = node("div", "card-nudge-head");
    head.append(node("h2", "card-nudge-title", view.title));
    root.append(head);
    if (view.needs.length > 0) {
      const list = node("ul", "card-nudge-needs");
      list.dataset["testid"] = "card-nudge-needs";
      for (const label of view.needs) list.append(node("li", "card-nudge-need", label));
      root.append(list);
    }
    if (view.line !== null) {
      const line = node("p", "card-nudge-line", view.line);
      line.dataset["testid"] = "card-nudge-line";
      root.append(line);
    }
    const actions = node("div", "card-nudge-actions");
    if (view.button) {
      const button = node("button", "btn-primary", CARD_NUDGE_BUTTON);
      button.type = "button";
      button.dataset["testid"] = "card-nudge-continue";
      button.disabled = opening;
      button.addEventListener("click", () => void openStripe());
      actions.append(button);
    }
    if (view.state === "setup" && lastStep !== null) {
      const step = lastStep;
      const link = node("button", "btn-primary", step.linkLabel);
      link.type = "button";
      link.dataset["testid"] = "card-nudge-next-step";
      link.addEventListener("click", () => step.open());
      actions.append(link);
    }
    if (actions.childElementCount > 0) root.append(actions);
    if (errorLine !== "") {
      const error = node("p", "admin-error card-nudge-error", errorLine);
      error.setAttribute("role", "alert");
      error.dataset["testid"] = "card-nudge-error";
      root.append(error);
    }
    schedulePoll();
  }

  async function openStripe(): Promise<void> {
    if (opening) return;
    // Inside the click, before any await: Stripe opens in its own tab and this one stays on Home.
    deps.beforeOpen?.();
    const tab = openOnboardingTab();
    errorLine = "";
    if (tab === null) {
      errorLine = TAB_BLOCKED_LINE;
      render();
      return;
    }
    opening = true;
    render();
    const outcome = await requestOnboardingTarget(
      tab,
      () => mintOnboardingLink(deps.apiBaseUrl(), deps.tenantId()),
      () => mode,
    );
    opening = false;
    if (!deps.isCurrent()) return;
    if (!outcome.ok) errorLine = outcome.message;
    render();
  }

  async function refresh(): Promise<void> {
    if (!deps.canShow()) {
      reset();
      return;
    }
    const mine = (sequence += 1);
    const result = await getCardPayments(deps.apiBaseUrl(), deps.tenantId());
    if (mine !== sequence || !deps.isCurrent()) return;
    if (result.status !== 200 || !Array.isArray(result.body.otherLocations)) {
      // Off for this deployment, not allowed, or a read that failed: no card. Settings still says why.
      reset();
      return;
    }
    const body: CardPaymentsForLocationDto = result.body;
    mode = body.mode;
    let step: ReturnType<CardNudgeDeps["missingStep"]> = null;
    if (body.thisLocation.kind === "not_set_up" || body.thisLocation.kind === "creating" || body.thisLocation.kind === "no_legal_entity") {
      await deps.prepareMissingStep();
      if (mine !== sequence || !deps.isCurrent()) return;
      step = deps.missingStep();
    }
    lastStep = step;
    lastView = cardNudgeView(body.thisLocation, true, step?.line ?? null);
    errorLine = "";
    opening = false;
    render();
  }

  return { refresh, reset };
}
